// =====================================================================
// tpCustomer — Opportunities self-tests
//
// Hand-rolled assertion suite, same shape as churn-metrics-tests.js.
// Covers the commission totalPct invariant (P0.1) and the MEDDPICCR
// derivation rules (P1.1) from the MEDDPICCR build spec.
//
// Two run modes:
//
//   1. Browser: auto-runs when ?oppTest=1 or localStorage.oppTest='1',
//      logging PASS / FAIL to console. window.tpOppSelfTest() runs on
//      demand from devtools.
//
//   2. Node CLI:
//        node -e "global.window={}; require('./tpCustFe/pages/opportunity-commission.js'); require('./tpCustFe/pages/opportunity-meddpiccr.js'); require('./tpCustFe/pages/opportunities-tests.js'); process.exit(window.tpOppSelfTest().failed === 0 ? 0 : 1)"
// =====================================================================

(function () {
  'use strict';
  const tpC = (typeof window !== 'undefined' && window.tpCommission);
  const tpM = (typeof window !== 'undefined' && window.tpMeddpiccr);
  const tpT = (typeof window !== 'undefined' && window.tpThresholds);
  if (!tpC) {
    if (typeof console !== 'undefined') console.error('opportunities-tests: tpCommission not available; load opportunity-commission.js first');
    return;
  }
  if (!tpM) {
    if (typeof console !== 'undefined') console.error('opportunities-tests: tpMeddpiccr not available; load opportunity-meddpiccr.js first');
    return;
  }
  if (!tpT) {
    if (typeof console !== 'undefined') console.error('opportunities-tests: tpThresholds not available; load opportunity-thresholds.js first');
    return;
  }

  const ALL_ROLE_KEYS = ['owner', 'sdr', 'se', 'partner'];

  function makeRunner() {
    let passed = 0, failed = 0;
    const failures = [];

    function assert(name, cond, detail) {
      if (cond) { passed++; }
      else {
        failed++;
        failures.push('✗ ' + name + (detail ? ' — ' + detail : ''));
      }
    }
    function eqDeep(a, b) {
      if (a === b) return true;
      if (typeof a !== typeof b) return false;
      if (typeof a !== 'object' || !a || !b) return false;
      const ak = Object.keys(a), bk = Object.keys(b);
      if (ak.length !== bk.length) return false;
      for (const k of ak) if (!eqDeep(a[k], b[k])) return false;
      return true;
    }
    function assertEq(name, actual, expected) {
      const ok = eqDeep(actual, expected);
      assert(name, ok, ok ? null : 'expected ' + JSON.stringify(expected) + ', got ' + JSON.stringify(actual));
    }
    return { assert, assertEq, summary: () => ({ passed, failed, failures }) };
  }

  function sumRendered(rates, roleKeys, showPartnerRow) {
    // Mirror of the tbody filter at opportunities.jsx:848 — what's rendered
    // is what should sum to totalPct.
    const visible = roleKeys.filter(k => k !== 'partner' || showPartnerRow);
    return visible.reduce((s, k) => s + (Number(rates[k]) || 0), 0);
  }

  function runAll() {
    const t = makeRunner();

    // ── totalPct === sum(rendered rows) for a partner deal ────────────
    const partnerRates = { owner: 8, sdr: 1, se: 0.5, partner: 15 };
    t.assertEq(
      'partner deal: totalPct sums all four rendered rows',
      tpC.commissionTotalPct(partnerRates, ALL_ROLE_KEYS, { showPartnerRow: true }),
      sumRendered(partnerRates, ALL_ROLE_KEYS, true)
    );
    t.assertEq(
      'partner deal: totalPct is exactly 24.5%',
      tpC.commissionTotalPct(partnerRates, ALL_ROLE_KEYS, { showPartnerRow: true }),
      24.5
    );

    // ── totalPct === sum(rendered rows) for a no-partner deal ──────────
    // This is the bug case — pre-fix, totalPct silently folded in the
    // 15% partner fallback even though the partner row wasn't rendered.
    const noPartnerRates = { owner: 8, sdr: 1, se: 0.5, partner: 15 };
    t.assertEq(
      'no-partner deal: totalPct excludes partner row',
      tpC.commissionTotalPct(noPartnerRates, ALL_ROLE_KEYS, { showPartnerRow: false }),
      sumRendered(noPartnerRates, ALL_ROLE_KEYS, false)
    );
    t.assertEq(
      'no-partner deal: totalPct is exactly 9.5% (no 15% phantom)',
      tpC.commissionTotalPct(noPartnerRates, ALL_ROLE_KEYS, { showPartnerRow: false }),
      9.5
    );

    // ── Default options ───────────────────────────────────────────────
    t.assertEq(
      'missing options arg: defaults to no-partner (showPartnerRow falsy)',
      tpC.commissionTotalPct(noPartnerRates, ALL_ROLE_KEYS),
      9.5
    );

    // ── Numeric coercion of string inputs from the editor ─────────────
    // The percentage inputs at opportunities.jsx:866-870 are <input
    // type="number">, whose value arrives as a string. The helper must
    // coerce so the displayed total tracks edits without an explicit
    // parse step at the call site.
    const stringRates = { owner: '8', sdr: '1', se: '0.5', partner: '15' };
    t.assertEq(
      'string rates from editor are coerced to numbers',
      tpC.commissionTotalPct(stringRates, ALL_ROLE_KEYS, { showPartnerRow: true }),
      24.5
    );

    // ── Empty / missing fields don't NaN the total ────────────────────
    const partialRates = { owner: 8, sdr: '', se: null, partner: undefined };
    t.assertEq(
      'empty / null / undefined rates contribute 0',
      tpC.commissionTotalPct(partialRates, ALL_ROLE_KEYS, { showPartnerRow: true }),
      8
    );

    // ═══════════════════════════════════════════════════════════════════
    // P1.1 — MEDDPICCR derivation
    // ═══════════════════════════════════════════════════════════════════

    const DEFAULT_SETTINGS = {
      meddpiccThresholds: { confidenceMin: { economicBuyer: 3, champion: 4 } },
    };
    const RAISED_SETTINGS = {
      meddpiccThresholds: { confidenceMin: { economicBuyer: 5, champion: 5 } },
    };
    const EMPTY_SETTINGS = null;

    // Most assertions below want the bare boolean per element, not the
    // rich {value, source, ...} shape that deriveMeddpiccr returns since
    // P1.4. `dv` flattens it. Tests that care about the source field
    // call tpM.deriveMeddpiccr directly.
    function dv() {
      const rich = tpM.deriveMeddpiccr.apply(null, arguments);
      const out = {};
      for (const k in rich) out[k] = rich[k].value;
      return out;
    }

    // ── confidenceFloor ──────────────────────────────────────────────
    t.assertEq('confidenceFloor: EB defaults to 3', tpM.confidenceFloor('Economic Buyer', DEFAULT_SETTINGS), 3);
    t.assertEq('confidenceFloor: Champion defaults to 4', tpM.confidenceFloor('Champion', DEFAULT_SETTINGS), 4);
    t.assertEq('confidenceFloor: EB tracks raised setting', tpM.confidenceFloor('Economic Buyer', RAISED_SETTINGS), 5);
    t.assertEq('confidenceFloor: Champion tracks raised setting', tpM.confidenceFloor('Champion', RAISED_SETTINGS), 5);
    // Spec requirement: a missing setting must NOT silently disable derivation.
    t.assertEq('confidenceFloor: missing settings fall back to 3 for EB', tpM.confidenceFloor('Economic Buyer', EMPTY_SETTINGS), 3);
    t.assertEq('confidenceFloor: missing settings fall back to 4 for Champion', tpM.confidenceFloor('Champion', EMPTY_SETTINGS), 4);
    // Roles other than EB/Champion do not drive E or C and should never auto-confirm.
    t.assert('confidenceFloor: other roles return Infinity',
      tpM.confidenceFloor('Influencer', DEFAULT_SETTINGS) === Infinity);

    // ── Economic Buyer derivation ────────────────────────────────────
    const baseOpp = { id: 'o1', meddpicc: {} };
    t.assertEq(
      'EB at confidence 3 derives true',
      dv(baseOpp,
        [{ oppId: 'o1', contactId: 'c1', role: 'Economic Buyer', roleConfidence: 3 }],
        DEFAULT_SETTINGS).economicBuyer,
      true
    );
    t.assertEq(
      'EB at confidence 2 derives false',
      dv(baseOpp,
        [{ oppId: 'o1', contactId: 'c1', role: 'Economic Buyer', roleConfidence: 2 }],
        DEFAULT_SETTINGS).economicBuyer,
      false
    );
    t.assertEq(
      'EB tracks raised floor (4 < 5 = false)',
      dv(baseOpp,
        [{ oppId: 'o1', contactId: 'c1', role: 'Economic Buyer', roleConfidence: 4 }],
        RAISED_SETTINGS).economicBuyer,
      false
    );
    t.assertEq(
      'no stakeholders => EB false',
      dv(baseOpp, [], DEFAULT_SETTINGS).economicBuyer,
      false
    );

    // ── Champion derivation ──────────────────────────────────────────
    t.assertEq(
      'Champion at confidence 4 derives true',
      dv(baseOpp,
        [{ oppId: 'o1', contactId: 'c1', role: 'Champion', roleConfidence: 4 }],
        DEFAULT_SETTINGS).champion,
      true
    );
    t.assertEq(
      'Champion at confidence 3 derives false (floor is 4)',
      dv(baseOpp,
        [{ oppId: 'o1', contactId: 'c1', role: 'Champion', roleConfidence: 3 }],
        DEFAULT_SETTINGS).champion,
      false
    );

    // ── Competition derivation ───────────────────────────────────────
    t.assertEq(
      'Competition: competitors + position both set => true',
      dv({ id: 'o1', meddpicc: {}, competitors: ['Acme'], competitivePosition: 'Leading' },
        [], DEFAULT_SETTINGS).competition,
      true
    );
    t.assertEq(
      'Competition: empty competitors => false',
      dv({ id: 'o1', meddpicc: {}, competitors: [], competitivePosition: 'Leading' },
        [], DEFAULT_SETTINGS).competition,
      false
    );
    t.assertEq(
      'Competition: missing position => false',
      dv({ id: 'o1', meddpicc: {}, competitors: ['Acme'], competitivePosition: '' },
        [], DEFAULT_SETTINGS).competition,
      false
    );
    t.assertEq(
      'Competition: competitors arriving as JSON string is parsed',
      dv({ id: 'o1', meddpicc: {}, competitors: '["Acme"]', competitivePosition: 'Behind' },
        [], DEFAULT_SETTINGS).competition,
      true
    );

    // ── Paper Process derivation ─────────────────────────────────────
    const fullCommercials = {
      id: 'o1', meddpicc: {},
      legalReview: 1, securityReview: 1, procurementInvolved: 1,
      proposalSent: '2026-04-01', contractSent: '2026-04-15',
    };
    t.assertEq(
      'Paper Process: full Commercials + one contract date => true',
      dv(fullCommercials, [], DEFAULT_SETTINGS).paperProcess,
      true
    );
    t.assertEq(
      'Paper Process: missing one boolean => false',
      dv(Object.assign({}, fullCommercials, { securityReview: 0 }), [], DEFAULT_SETTINGS).paperProcess,
      false
    );
    t.assertEq(
      'Paper Process: blank proposalSent => false',
      dv(Object.assign({}, fullCommercials, { proposalSent: '' }), [], DEFAULT_SETTINGS).paperProcess,
      false
    );
    t.assertEq(
      'Paper Process: no contract date => false',
      dv(Object.assign({}, fullCommercials, { contractSent: '' }), [], DEFAULT_SETTINGS).paperProcess,
      false
    );

    // ── Bridge mode (only I left now — DC/DP got structured fields in P1.3)
    const bridgedOpp = { id: 'o1', meddpicc: { identifyPain: 1 } };
    const bridgedDerived = dv(bridgedOpp, [], DEFAULT_SETTINGS);
    t.assertEq('Bridge: I still follows opp.meddpicc.identifyPain',
      bridgedDerived.identifyPain, true);

    // ── P1.3: DC derives from opp.decisionCriteria ───────────────────
    t.assertEq(
      'DC: blank decisionCriteria => false',
      dv({ id: 'o1', meddpicc: {} }, [], DEFAULT_SETTINGS).decisionCriteria,
      false
    );
    t.assertEq(
      'DC: whitespace-only decisionCriteria => false',
      dv({ id: 'o1', meddpicc: {}, decisionCriteria: '   \n\t  ' }, [], DEFAULT_SETTINGS).decisionCriteria,
      false
    );
    t.assertEq(
      'DC: non-empty decisionCriteria => true',
      dv({ id: 'o1', meddpicc: {}, decisionCriteria: '· SSO required' }, [], DEFAULT_SETTINGS).decisionCriteria,
      true
    );
    t.assertEq(
      'DC: legacy opp.meddpicc.decisionCriteria toggle is IGNORED',
      dv({ id: 'o1', meddpicc: { decisionCriteria: 1 } }, [], DEFAULT_SETTINGS).decisionCriteria,
      false
    );

    // ── P1.3: DP derives from opp.decisionProcess ────────────────────
    t.assertEq(
      'DP: blank decisionProcess => false',
      dv({ id: 'o1', meddpicc: {} }, [], DEFAULT_SETTINGS).decisionProcess,
      false
    );
    t.assertEq(
      'DP: non-empty decisionProcess => true',
      dv({ id: 'o1', meddpicc: {}, decisionProcess: '1. Eng eval\n2. CISO sign-off' }, [], DEFAULT_SETTINGS).decisionProcess,
      true
    );
    t.assertEq(
      'DP: legacy opp.meddpicc.decisionProcess toggle is IGNORED',
      dv({ id: 'o1', meddpicc: { decisionProcess: 1 } }, [], DEFAULT_SETTINGS).decisionProcess,
      false
    );

    // ── P1.2: M derives from opp.metrics ─────────────────────────────
    t.assertEq(
      'M: empty metrics column => false',
      dv({ id: 'o1', meddpicc: {} }, [], DEFAULT_SETTINGS).metrics,
      false
    );
    t.assertEq(
      'M: metrics with metricLabel => true',
      dv({ id: 'o1', meddpicc: {}, metrics: { metricLabel: 'Net new ARR' } }, [], DEFAULT_SETTINGS).metrics,
      true
    );
    t.assertEq(
      'M: metrics with blank metricLabel => false',
      dv({ id: 'o1', meddpicc: {}, metrics: { metricLabel: '   ', baseline: 'X' } }, [], DEFAULT_SETTINGS).metrics,
      false
    );
    t.assertEq(
      'M: legacy opp.meddpicc.metrics toggle is IGNORED (no structured field)',
      dv({ id: 'o1', meddpicc: { metrics: 1 } }, [], DEFAULT_SETTINGS).metrics,
      false
    );
    t.assertEq(
      'M: metrics arriving as JSON string is parsed',
      dv({ id: 'o1', meddpicc: {}, metrics: '{"metricLabel":"X"}' }, [], DEFAULT_SETTINGS).metrics,
      true
    );

    // ── P1.2: R derives from opportunity_risks ───────────────────────
    t.assertEq(
      'R: no risks logged => false (empty register is not "all clear")',
      dv({ id: 'o1', meddpicc: {} }, [], DEFAULT_SETTINGS, []).risk,
      false
    );
    t.assertEq(
      'R: open risk missing owner => false',
      dv({ id: 'o1', meddpicc: {} }, [], DEFAULT_SETTINGS,
        [{ id: 'r1', oppId: 'o1', risk: 'X', owner: '', mitigation: 'Y', status: 'open' }]).risk,
      false
    );
    t.assertEq(
      'R: open risk missing mitigation => false',
      dv({ id: 'o1', meddpicc: {} }, [], DEFAULT_SETTINGS,
        [{ id: 'r1', oppId: 'o1', risk: 'X', owner: 'Alice', mitigation: '', status: 'open' }]).risk,
      false
    );
    t.assertEq(
      'R: every open risk has owner+mitigation => true',
      dv({ id: 'o1', meddpicc: {} }, [], DEFAULT_SETTINGS,
        [{ id: 'r1', oppId: 'o1', risk: 'X', owner: 'Alice', mitigation: 'Plan A', status: 'open' }]).risk,
      true
    );
    t.assertEq(
      'R: mitigated risks are ignored (skipped, not gating)',
      dv({ id: 'o1', meddpicc: {} }, [], DEFAULT_SETTINGS, [
        { id: 'r1', oppId: 'o1', risk: 'X', owner: 'Alice', mitigation: 'Plan A', status: 'open' },
        { id: 'r2', oppId: 'o1', risk: 'Y', owner: '',      mitigation: '',       status: 'mitigated' },
      ]).risk,
      true
    );
    t.assertEq(
      'R: accepted risks are ignored too',
      dv({ id: 'o1', meddpicc: {} }, [], DEFAULT_SETTINGS, [
        { id: 'r1', oppId: 'o1', risk: 'X', owner: 'Alice', mitigation: 'Plan A', status: 'open' },
        { id: 'r2', oppId: 'o1', risk: 'Y', owner: '',      mitigation: '',       status: 'accepted' },
      ]).risk,
      true
    );
    t.assertEq(
      'R: status defaults to open when missing — incomplete row still gates',
      dv({ id: 'o1', meddpicc: {} }, [], DEFAULT_SETTINGS,
        [{ id: 'r1', oppId: 'o1', risk: 'X', owner: '', mitigation: '' }]).risk,
      false
    );
    t.assertEq(
      'R: legacy opp.meddpicc.risk toggle is IGNORED (structured-only)',
      dv({ id: 'o1', meddpicc: { risk: 1 } }, [], DEFAULT_SETTINGS, []).risk,
      false
    );

    // The derived-state spec requires that a bare opp.meddpicc.economicBuyer
    // toggle alone (no stakeholder) does NOT flip E — derivation must
    // beat the legacy click on derived elements.
    t.assertEq(
      'E ignores opp.meddpicc.economicBuyer when no EB stakeholder',
      dv({ id: 'o1', meddpicc: { economicBuyer: 1 } }, [], DEFAULT_SETTINGS).economicBuyer,
      false
    );

    // ═══════════════════════════════════════════════════════════════════
    // P1.4 — Override path
    // ═══════════════════════════════════════════════════════════════════

    // Override forces a derived-false element ON, with attribution.
    const overrideOnOpp = {
      id: 'o1',
      meddpicc: {},
      meddpiccOverrides: {
        economicBuyer: {
          value: true, source: 'override-on',
          by: 'u_alice', at: '2026-05-25T10:00:00Z',
          reason: 'EB confirmed verbally on the demo call but not yet entered.',
        },
      },
    };
    const onResult = tpM.deriveMeddpiccr(overrideOnOpp, [], DEFAULT_SETTINGS);
    t.assertEq('Override-on: value forced true',                onResult.economicBuyer.value,  true);
    t.assertEq('Override-on: source recorded',                  onResult.economicBuyer.source, 'override-on');
    t.assertEq('Override-on: reason carries through',           onResult.economicBuyer.reason, 'EB confirmed verbally on the demo call but not yet entered.');
    t.assertEq('Override-on: by recorded',                      onResult.economicBuyer.by,     'u_alice');
    t.assertEq('Non-overridden elements stay source=derived',   onResult.metrics.source,       'derived');

    // Override forces a derived-true element OFF.
    const overrideOffOpp = {
      id: 'o1', meddpicc: {}, competitors: ['Acme'], competitivePosition: 'Leading',
      meddpiccOverrides: {
        competition: {
          value: false, source: 'override-off',
          by: 'u_bob', at: '2026-05-25T11:00:00Z',
          reason: 'Position was set as Leading but customer just disclosed they evaluated Acme last quarter.',
        },
      },
    };
    const offResult = tpM.deriveMeddpiccr(overrideOffOpp, [], DEFAULT_SETTINGS);
    t.assertEq('Override-off: derived-true element forced false', offResult.competition.value,  false);
    t.assertEq('Override-off: source recorded',                   offResult.competition.source, 'override-off');

    // Malformed override (missing reason, bad source) is ignored — fall through to derived.
    const badOverrideOpp = {
      id: 'o1', meddpicc: {}, competitors: ['Acme'], competitivePosition: 'Leading',
      meddpiccOverrides: {
        competition: { value: false, source: 'bogus', by: 'u_alice', at: '...', reason: '...' },
      },
    };
    t.assertEq('Bad source: override ignored, falls through to derived true',
      tpM.deriveMeddpiccr(badOverrideOpp, [], DEFAULT_SETTINGS).competition.value, true);
    t.assertEq('Bad source: source reports derived',
      tpM.deriveMeddpiccr(badOverrideOpp, [], DEFAULT_SETTINGS).competition.source, 'derived');

    // Score reflects overrides. EB derived false (no stakeholder) but
    // override-on forces it true — score becomes 1.
    t.assertEq(
      'score: override-on element counts toward total',
      tpM.meddScoreFromDerived(
        tpM.deriveMeddpiccr(overrideOnOpp, [], DEFAULT_SETTINGS),
        ['economicBuyer']
      ),
      { got: 1, total: 1 }
    );

    // ── meddScoreFromDerived ─────────────────────────────────────────
    const MEDD_KEY_ORDER = [
      'metrics', 'economicBuyer', 'decisionCriteria', 'decisionProcess',
      'paperProcess', 'identifyPain', 'champion', 'competition', 'risk',
    ];
    t.assertEq(
      'score: empty deal scores 0/9',
      tpM.meddScoreFromDerived(dv({ id: 'o1', meddpicc: {} }, [], DEFAULT_SETTINGS), MEDD_KEY_ORDER),
      { got: 0, total: 9 }
    );
    const richOpp = Object.assign({}, fullCommercials, {
      competitors: ['Acme'], competitivePosition: 'Leading',
      metrics: { metricLabel: 'Net new ARR', target: '+£500k' },
      decisionCriteria: '· SSO required\n· Compliance: SOC2',
      decisionProcess:  '1. Eng eval\n2. CISO sign-off\n3. Procurement',
      meddpicc: { identifyPain: 1 },
    });
    const richStakeholders = [
      { oppId: 'o1', contactId: 'c1', role: 'Economic Buyer', roleConfidence: 4 },
      { oppId: 'o1', contactId: 'c2', role: 'Champion',       roleConfidence: 5 },
    ];
    const richRisks = [
      { id: 'r1', oppId: 'o1', risk: 'Single-threaded', owner: 'Maya', mitigation: 'Multi-thread sponsor', status: 'open' },
    ];
    t.assertEq(
      'score: fully-qualified deal scores 9/9 (M from metrics; R from risks)',
      tpM.meddScoreFromDerived(dv(richOpp, richStakeholders, DEFAULT_SETTINGS, richRisks), MEDD_KEY_ORDER),
      { got: 9, total: 9 }
    );

    // ═══════════════════════════════════════════════════════════════════
    // P2.2 — Thresholds consolidation
    // ═══════════════════════════════════════════════════════════════════

    // Defaults when settings is missing entirely.
    const defT = tpT.meddpiccForecastThresholds(null);
    t.assertEq('thresholds default: bestCase = 4',    defT.bestCase,    4);
    t.assertEq('thresholds default: commit = 6',      defT.commit,      6);
    t.assertEq('thresholds default: closed = 7',      defT.closed,      7);
    t.assertEq('thresholds default: atRiskBelow = 4', defT.atRiskBelow, 4);

    // Custom settings override the defaults but keep the rest.
    const customT = tpT.meddpiccForecastThresholds({
      meddpiccThresholds: { commit: 5, atRiskBelow: 3 },
    });
    t.assertEq('thresholds custom: commit overridden', customT.commit, 5);
    t.assertEq('thresholds custom: atRiskBelow overridden', customT.atRiskBelow, 3);
    t.assertEq('thresholds custom: bestCase still default', customT.bestCase, 4);
    t.assertEq('thresholds custom: closed still default',   customT.closed,   7);

    // stageForecastMin returns the same shape STAGE_FORECAST_MIN used to.
    const stageMin = tpT.stageForecastMin(null);
    t.assertEq('stageForecastMin: Pipeline = 0',    stageMin.Pipeline,    0);
    t.assertEq('stageForecastMin: Best case = 4',   stageMin['Best case'], 4);
    t.assertEq('stageForecastMin: Commit = 6',      stageMin.Commit,      6);
    t.assertEq('stageForecastMin: Closed = 7',      stageMin.Closed,      7);

    // Non-integer values fall through to defaults (so a corrupt settings
    // row can't silently weaken thresholds).
    t.assertEq(
      'thresholds: non-integer commit falls back to 6',
      tpT.meddpiccForecastThresholds({ meddpiccThresholds: { commit: 'six' } }).commit,
      6
    );

    return t.summary();
  }

  function tpOppSelfTest() {
    const r = runAll();
    if (typeof console !== 'undefined') {
      if (r.failed === 0) console.log('opportunities-tests: ' + r.passed + ' passed');
      else {
        console.error('opportunities-tests: ' + r.failed + ' FAILED, ' + r.passed + ' passed');
        for (const line of r.failures) console.error('  ' + line);
      }
    }
    return r;
  }

  if (typeof window !== 'undefined') window.tpOppSelfTest = tpOppSelfTest;

  // Auto-run in the browser only when explicitly opted in, to avoid
  // slowing normal page loads. Matches the churn-metrics-tests gate.
  if (typeof window !== 'undefined' && typeof location !== 'undefined') {
    const onParam = /[?&]oppTest=1\b/.test(location.search);
    let onStorage = false;
    try { onStorage = typeof localStorage !== 'undefined' && localStorage.getItem('oppTest') === '1'; } catch (_) {}
    if (onParam || onStorage) tpOppSelfTest();
  }
})();
